import type Currency from './currency';
import currencies from '../../i18n/currencies';
import { languageCodes } from '../../i18n/enums/languageCodes';
import type { currencyCodes } from '../../i18n/enums/currencyCodes';
import { defaultCountryCode } from '../../i18n/enums/countryCodes';
import { isProduction } from '../../helpers/env/checkEnvironmentType';
import { LANGUAGE_AND_COUNTRY_LENGTH } from '../../constants/localization';
import type { CountryConfigurationOptions } from './countryConfigurationOptions';
import type {
  LanguageIdentification,
  ParcelPerformByLanguage,
  BloomreachKeyByLanguage,
  CountryCodeValue,
} from './localizationHelperTypes';

const STAGE_BLOOMREACH_BY_LANGUAGE: BloomreachKeyByLanguage[] = [
  {
    key: 'd1a36480-2003-11ea-a284-7641af141315',
  },
];

class CountryLocalization {

  readonly DOMAIN_PREFIX = 'www.expondo';
  readonly STORE_PREFIX = 'expondo';
  static readonly TYPESENSE_INDEX_NAME_SEPARATOR = '.';
  public prodDomain: string;
  public topLevelDomain: string;
  public isDefault: boolean = false;
  public fallbacks: {[key: string] : string[]} = {};
  public countryCode: CountryCodeValue;
  public label: string;
  public currencyCode: ValueOf<typeof currencyCodes>;
  public enableNativeRouting: boolean;
  public storeId: string;

  private _parcelPerformByLanguage: Array<ParcelPerformByLanguage>;
  private _configBloomreachByLanguage: Array<BloomreachKeyByLanguage>;
  private _languageAndCountryFallbacks: Array<LanguageIdentification>;
  private _customDomainEnding: string | null;
  private _customStoreEnding: string | null;
  private _languageAndCountries: string[] = [];
  private _languages: Array<ValueOf<typeof languageCodes>>;

  constructor (countryConfig: CountryConfigurationOptions) {

    this._languages = countryConfig.languages;
    this._parcelPerformByLanguage = countryConfig.parcelPerformByLanguage;
    this._configBloomreachByLanguage = countryConfig.bloomreachByLanguage ?? [];
    this._languageAndCountryFallbacks = countryConfig.languageAndCountryFallbacks ?? [];
    this._customDomainEnding = countryConfig.customDomainEnding ?? null;
    this._customStoreEnding = countryConfig.customStoreEnding ?? null;

    this.countryCode = countryConfig.countryCode;
    this.label = countryConfig.label;
    this.currencyCode = countryConfig.currencyCode;
    this.enableNativeRouting = countryConfig.enableNativeRouting;
    this.isDefault = this.countryCode === defaultCountryCode;
    this.prodDomain = `${this.DOMAIN_PREFIX}.${this._customDomainEnding || this.countryCode}`;
    this.topLevelDomain = this._customDomainEnding || this.countryCode;
    this.storeId = `${this.STORE_PREFIX}_${this._customStoreEnding || this.countryCode}`;

    for (const language of this._languages) {

      this._languageAndCountries.push(this.getLanguageAndCountry(language));

    }

  }

  get flagFile(): string {

    return `flag_${this.countryCode}.svg`;

  }

  get isMultiLanguage(): boolean {

    return Object.entries(this._languageAndCountries).length > 1;

  }

  get currency() : Currency {

    return currencies[this.currencyCode];

  }

  public getVueI18nFallbacks(): {[key: string] : string[]} {

    const fallbacks: {[key: string] : string[]} = {};

    for (const { language, countryCode } of this._languageAndCountryFallbacks) {

      const source = this.getLanguageAndCountry(language);
      fallbacks[source] = [this.getLanguageAndCountry(language, countryCode)];

    }

    return fallbacks;

  }

  public isDefaultLanguage (language: ValueOf<typeof languageCodes>| string): boolean {

    return this.getDefaultLanguage() === language.toLowerCase();

  }

  public isDefaultLanguageAndCountry (languageAndCountry: string): boolean {

    return this.getDefaultLanguageAndCountry()?.toLocaleLowerCase() === languageAndCountry.toLocaleLowerCase();

  }

  public getDefaultLanguage() {

    return this._languages[0];

  }

  public getDefaultLanguageAndCountry() {

    return this._languageAndCountries[0];

  }

  public getLanguageAndCountry(
    language: ValueOf<typeof languageCodes> | string | null | undefined = null,
    countryCode: Maybe<CountryCodeValue> = null): string {

    language = language || this.getDefaultLanguage();
    countryCode = countryCode || this.countryCode;

    return `${language?.toLowerCase()}-${countryCode?.toUpperCase()}`;

  }

  public determineLanguageAndCountry(languageAndCountryOrLanguage: string | null | undefined): string {

    const defaultLanguageAndCountry = this.getDefaultLanguageAndCountry();

    if (!languageAndCountryOrLanguage && defaultLanguageAndCountry) {

      return defaultLanguageAndCountry;

    }

    if (languageAndCountryOrLanguage?.length === LANGUAGE_AND_COUNTRY_LENGTH) {

      return languageAndCountryOrLanguage;

    }

    return this.getLanguageAndCountry(languageAndCountryOrLanguage);

  }

  public getLanguageFromExistingLanguageAndCountry(languageAndCountry: string): ValueOf<typeof languageCodes> | null {

    if (this._languageAndCountries.includes(languageAndCountry)) {

      return CountryLocalization.getLanguageFromLanguageAndCountry(languageAndCountry);

    }

    return null;

  }

  public static getLanguageFromLanguageAndCountry(languageAndCountry: string): ValueOf<typeof languageCodes> | null {

    const language = languageAndCountry.split('-')[0];

    if (language && (Object.values(languageCodes) as string[]).includes(language)) {

      return language as ValueOf<typeof languageCodes>;

    }

    return null;

  }

  /**
   * Returns a list of 5 characters locale, like `nl-BE`, `fr-FR` or `en-GB`
  */
  public getLanguageAndCountries() {

    return this._languageAndCountries;

  }

  /**
   * Returns a list of 2 characters languages, like 'sk', 'en', 'fr', 'nl', 'pt'
   */
  public getLanguages() {

    return this._languages;

  }

  /**
   *
   * Returns a list of accepted languages, each one in 5 characters format.
   * Accepted languages includes the language derived from active language, plus
   * additional accepted languages, which might be different from fallbacks languages.
   */
  public getAcceptedLanguages(activeLanguage: ValueOf<typeof languageCodes> | null | undefined = null): Array<string> {

    activeLanguage = activeLanguage || this.getDefaultLanguage();
    const acceptedLanguages = [this.getLanguageAndCountry(activeLanguage)];

    return acceptedLanguages;

  }

  public getParcelPerformId(language: ValueOf<typeof languageCodes>): string | null {

    for (const parcelPerformByLanguage of this._parcelPerformByLanguage) {

      if (parcelPerformByLanguage.language === language) {

        // handling for multi language countries
        return parcelPerformByLanguage.parcelPerformId;

      }

    }

    // handling for single language countries
    return this._parcelPerformByLanguage?.[0]?.parcelPerformId || null;

  }

  public getBloomreachDetails(languageAndCountry: string): BloomreachKeyByLanguage {

    const language = this.getLanguageFromExistingLanguageAndCountry(languageAndCountry);
    const bloomreachKeys = isProduction()
      ? this._configBloomreachByLanguage
      : STAGE_BLOOMREACH_BY_LANGUAGE;
    const hasMultiLanguageData = bloomreachKeys.length > 1;

    if (hasMultiLanguageData && language) {

      for (const bloomreachKey of bloomreachKeys) {

        if (bloomreachKey.language === language) {

          return bloomreachKey;

        }

      }

    }

    return bloomreachKeys[0] || { key: '' };

  }

  public hasNativeRouteRedirection(): boolean {

    return this.enableNativeRouting;

  }

}

export default CountryLocalization;
